import * as React from 'react';
import { getAnsprechpartnerLabel } from 'utils/labels';
import { DefaultButton, Dialog, DialogFooter, DialogType, PrimaryButton } from '@fluentui/react';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { getTheme, ITheme, mergeStyleSets } from '@fluentui/react/lib/Styling';
import { useKunde } from 'api/kunde';

const theme: ITheme = getTheme();
const { palette } = theme;

const styles = mergeStyleSets({
    table: {
        width: '100%',
        borderSpacing: 0,
    },
    header: {
        fontWeight: 700,
    },
    headerTd: {
        paddingLeft: 4,
    },
    td: {
        padding: 5,
    },
    row: {
        cursor: 'pointer',
        selectors: {
            '&:hover': { backgroundColor: palette.neutralLight },
        },
    },
    markAsSentCheckbox: {
        marginTop: 8,
        fontWeight: 700,
        marginLeft: 5,
        marginBottom: 20,
    },
    checkbox: {},
});

interface SendMailSelectProps {
    title?: string;
    kNr: number;
    kaNr?: number;
    eRechnungDefault?: boolean;
    markAsSentDisabled?: boolean;
    markAsSentLabel?: string;
    markForExportDisabled?: boolean;
    markForExportDefault?: boolean;
    markForExportLabel?: string;
    onSubmit: (kaNrListTo: number[], kaNrListCC: number[], markAsSent: boolean, markForExport: boolean) => void;
    onCancel: () => void;
}

export const SendMail: React.FC<SendMailSelectProps> = ({
    title = 'Empfänger',
    kNr,
    kaNr,
    eRechnungDefault = false,
    markAsSentDisabled,
    markAsSentLabel,
    markForExportDisabled,
    markForExportDefault = false,
    markForExportLabel,
    onSubmit,
    onCancel,
}) => {
    const [kaNrListTo, setKaNrListTo] = React.useState<number[]>(kaNr ? [kaNr] : []);
    const [kaNrListCC, setKaNrListCC] = React.useState<number[]>([]);

    const kunde = useKunde(
        {
            nr: kNr,
            withAnsprechpartnerList: true,
        },
        {
            onSuccess: (kunde2) => {
                if (eRechnungDefault && kunde2.ansprechpartnerList) {
                    setKaNrListTo(kunde2.ansprechpartnerList.filter((a) => a.eRechnung).map((a) => a.nr));
                }
            },
        },
    );

    const [markAsSent, setMarkAsSent] = React.useState(!markAsSentDisabled);
    const [markForExport, setMarkForExport] = React.useState(markForExportDefault);

    const toggleTo = (nr: number) => {
        if (!kaNrListTo.includes(nr) && kaNrListCC.includes(nr)) {
            setKaNrListCC(kaNrListCC.filter((n) => n !== nr));
        }

        const newKaNrListTo = kaNrListTo.filter((n) => n !== nr);
        setKaNrListTo(kaNrListTo.includes(nr) ? newKaNrListTo : [...newKaNrListTo, nr]);
    };

    const toggleCC = (nr: number) => {
        if (!kaNrListCC.includes(nr) && kaNrListTo.includes(nr)) {
            setKaNrListTo(kaNrListTo.filter((n) => n !== nr));
        }

        const newKaNrListCC = kaNrListCC.filter((n) => n !== nr);
        setKaNrListCC(kaNrListCC.includes(nr) ? newKaNrListCC : [...newKaNrListCC, nr]);
    };

    return (
        <Dialog
            hidden={false}
            title={title}
            dialogContentProps={{
                type: DialogType.normal,
            }}
            modalProps={{
                isBlocking: true,
            }}
            minWidth={800}
            onDismiss={onCancel}
        >
            {markAsSentLabel && (
                <Checkbox
                    className={styles.markAsSentCheckbox}
                    label={markAsSentLabel}
                    checked={markAsSent}
                    disabled={markAsSentDisabled}
                    onChange={(event, checked) => {
                        setMarkAsSent(checked || false);
                    }}
                />
            )}
            {markForExportLabel && (
                <Checkbox
                    className={styles.markAsSentCheckbox}
                    label={markForExportLabel}
                    checked={markForExport}
                    disabled={markForExportDisabled}
                    onChange={(event, checked) => {
                        setMarkForExport(checked || false);
                    }}
                />
            )}

            <table className={styles.table}>
                <thead>
                    <tr className={styles.header}>
                        <td className={styles.headerTd}>An</td>
                        <td className={styles.headerTd}>CC</td>
                        <td className={styles.headerTd}>Ansprechpartner</td>
                        <td className={styles.headerTd}>Position</td>
                        <td className={styles.headerTd}>E-Mail</td>
                    </tr>
                </thead>
                {kunde.data?.mail && (
                    <tr className={styles.row}>
                        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                        <td className={styles.td} onMouseUp={() => toggleTo(-1)}>
                            <Checkbox className={styles.checkbox} checked={kaNrListTo.includes(-1)} />
                        </td>
                        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                        <td className={styles.td} onMouseUp={() => toggleCC(-1)}>
                            <Checkbox className={styles.checkbox} checked={kaNrListCC.includes(-1)} />
                        </td>
                        <td className={styles.td}>{kunde.data?.name1 + ' ' + kunde.data?.name2}</td>
                        <td className={styles.td}>&nbsp;</td>
                        <td className={styles.td}>{kunde.data?.mail}</td>
                    </tr>
                )}
                {kunde.data?.ansprechpartnerList
                    ?.sort((a, b) => b.nr - a.nr)
                    .map((ansprechpartner) => (
                        <tr className={styles.row}>
                            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                            <td className={styles.td} onMouseUp={() => toggleTo(ansprechpartner.nr)}>
                                <Checkbox
                                    className={styles.checkbox}
                                    checked={kaNrListTo.includes(ansprechpartner.nr)}
                                />
                            </td>
                            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                            <td className={styles.td} onMouseUp={() => toggleCC(ansprechpartner.nr)}>
                                <Checkbox
                                    className={styles.checkbox}
                                    checked={kaNrListCC.includes(ansprechpartner.nr)}
                                />
                            </td>
                            <td className={styles.td}>{getAnsprechpartnerLabel(ansprechpartner)}</td>
                            <td className={styles.td}>{ansprechpartner.position}</td>
                            <td className={styles.td}>{ansprechpartner.mail}</td>
                        </tr>
                    ))}
            </table>
            <DialogFooter>
                <DefaultButton onClick={onCancel} text="Abbrechen" />
                <PrimaryButton
                    disabled={!kunde.isFetched}
                    onClick={() => onSubmit(kaNrListTo, kaNrListCC, markAsSent, markForExport)}
                    text="Senden"
                />
            </DialogFooter>
        </Dialog>
    );
};
